import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';

import { AuditService } from '@app/services/audit.service';
import { RenewalsService } from '@app/services/renewals.service';
import { RestapiService } from '@app/services/restapi.service';
import { AuditableEntityType } from '@shared/enums/auditable-entity-type';
import { SnackbarErrorMessage } from '@ui-components/components/customized-snackbar/snackbar-message.enum';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';

import {
  getRenewalChangesHistory,
  getRenewalChangesHistoryFailure,
  getRenewalChangesHistorySuccess,
  loadRenewalDetails,
  loadRenewalDetailsSucceded,
  loadRenewalStrategyForUnit,
  loadRenewalStrategyForUnitFailed,
  loadRenewalStrategyForUnitSucceded,
  loadRenewalsList,
  loadRenewalsListFailure,
  loadRenewalsListSucceded,
  saveRenewalDetails,
  saveRenewalDetailsFailure,
} from './renewals.actions';

@Injectable()
export class RenewalsEffects {
  loadRenewalsList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadRenewalsList),
      switchMap(action =>
        this.renewalsService.getRenewalList(action.propertyId).pipe(
          map(renewalsList => loadRenewalsListSucceded({ renewalsList: renewalsList || [] })),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.RetrievingRenewalsList);
            return of(loadRenewalsListFailure());
          })
        )
      )
    )
  );

  loadRenewalDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadRenewalDetails),
      switchMap(action =>
        this.renewalsService.getRenewalDetails(action.renewalId).pipe(
          map(renewalDetails => loadRenewalDetailsSucceded({ renewalDetails })),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.RetrievingRenewalData);
            return of(loadRenewalsListFailure());
          })
        )
      )
    )
  );

  loadRenewalStrategyForUnit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadRenewalStrategyForUnit),
      switchMap(action =>
        this.renewalsService.renewalTargetForUnitId(action.unitId).pipe(
          map(unitTypeRentTarget => loadRenewalStrategyForUnitSucceded({ unitTypeRentTarget })),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.RetrievingRenewalStrategy);
            return of(loadRenewalStrategyForUnitFailed());
          })
        )
      )
    )
  );

  updateRenewalDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveRenewalDetails),
      mergeMap(action =>
        this.renewalsService.updateRenewalDetails(action.renewalModel).pipe(
          mergeMap(renewalDetails => [
            loadRenewalDetailsSucceded({ renewalDetails }),
            getRenewalChangesHistory({ objectId: action.renewalModel.id }),
          ]),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.UpdatingRenewal);
            return of(saveRenewalDetailsFailure());
          })
        )
      )
    )
  );

  getChangesHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getRenewalChangesHistory),
      switchMap(action =>
        this.auditService.getChangesHistory(action.objectId, AuditableEntityType.Renewal).pipe(
          map(auditModelList => getRenewalChangesHistorySuccess({ auditModelList })),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.RetrievingRenewalChangesHistory);
            return of(getRenewalChangesHistoryFailure());
          })
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private snackbarService: SnackbarService,
    private restApiService: RestapiService,
    private renewalsService: RenewalsService,
    private auditService: AuditService
  ) {}
}
